<!--CopyPriceWindowModal.vue -->
<template>
  <div
    class="modal fade"
    id="copyPriceWindowModal"
    tabindex="-1"
    aria-labelledby="copyPriceWindowModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content text-start">
        <div class="modal-header">
          <i class="fa fa-copy fa-lg action-icon icon-margin"></i>
          <h5 class="modal-title" id="copyPriceWindowModalLabel">
            Copy Price Window Settings
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body text-start">
          <form>
            <div class="mb-3">
              <label class="title"> Copy from Price Window: </label>
              <p class="copy-from">{{ copyFrom }}</p>
            </div>
            <div class="mb-3">
              <label class="title"> Copy to Price Window: </label>
              <select
                class="form-select"
                v-model="selectedPricingWindow"
                @change="handleWindowChange"
              >
                <option
                  v-for="(window, index) in filteredPricingWindows"
                  :key="index"
                  :value="window.id"
                >
                  {{ window.name || '' }}
                </option>
                <option value="-1">{{ createNewPricingWindow }}</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="title">
                Modify the price adjustment percentage by this amount:
              </label>
              <div class="input-group">
                <input
                  class="form-control"
                  type="number"
                  value="0"
                  min="1"
                  v-model="priceAdjustmentValue"
                />
                <span class="input-group-text">%</span>
              </div>
              <p class="adjustment-info">
                {{ adjustmentInfo }}
              </p>
            </div>
          </form>
        </div>
        <div class="mb-3 ms-3" v-if="strategyType === 3">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              v-model="keepExistingWindows"
              id="keepExistingWindows"
            />
            <label class="form-check-label ms-2" for="keepExistingWindows">
              Keep existing time windows.
            </label>
            <i
              class="fa fa-info-circle info-icon keep-existing-windows-info ms-1"
            >
            </i>
          </div>
        </div>
        <div class="modal-footer text-start">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="copyPriceWindow"
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PriceWindow } from '@/api';
import { defineComponent, ref, computed, watchEffect, PropType } from 'vue';
import { Modal } from 'bootstrap';
import { useToast } from 'vue-toastification';

export default defineComponent({
  name: 'CopyPriceWindowModal',
  props: {
    pricingWindows: {
      type: Array as () => PriceWindow[],
      required: true,
    },
    pricingWindow: {
      type: [Object, null] as PropType<PriceWindow | null>,
      required: true,
    },
    strategyType: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const adjustmentInfo =
      '(eg. 75% would be adjusted up to 475%. Any fixed $ amounts will not be adjusted.)';
    const createNewPricingWindow = '<Create new Pricing Window>';
    const zeroPercentAdjustmentMessage =
      'This percent adjustment would cause some price adjustments to be 0% or lower.';

    const priceAdjustmentValue = ref(0);
    const toast = useToast();
    const keepExistingWindows = ref(true);

    const copyFrom = computed(() => {
      return props.pricingWindow ? props.pricingWindow.description : '';
    });

    const filteredPricingWindows = computed(() => {
      return props.pricingWindows.filter(
        (pw) => props.pricingWindow && pw.id !== props.pricingWindow.id
      );
    });

    const copyToPricingWindow = ref<number | null>(
      filteredPricingWindows.value[0]?.id ?? -1
    );

    const handleWindowChange = (e: any) => {
      copyToPricingWindow.value = Number(e.target.value);
    };

    const resetValues = () => {
      copyToPricingWindow.value = filteredPricingWindows.value[0]?.id ?? -1;
      priceAdjustmentValue.value = 0;
      keepExistingWindows.value = true;
    };

    watchEffect(() => {
      resetValues();
    });

    const hideModal = () => {
      const modalElement = document.getElementById('copyPriceWindowModal');
      if (modalElement) {
        const bootstrapModal =
          Modal.getInstance(modalElement) || new Modal(modalElement);
        bootstrapModal.hide();
      }
    };

    const copyPriceWindow = async () => {
      try {
        if (copyToPricingWindow.value?.toString() === '-1') {
          const validIds = props.pricingWindows
            .map((pw) => pw.id)
            .filter(
              (n): n is number => typeof n === 'number' && Number.isFinite(n)
            );
          const maxId = validIds.length ? Math.max(...validIds) : -1;
          const nextId = maxId + 1;
          const newPricingWindow = {
            ...props.pricingWindow, // Clone the current pricing window
            id: Number.isFinite(nextId) && nextId > 0 ? nextId : -1,
            priceAdjustment: priceAdjustmentValue.value,
          };

          emit(
            'price-window-copied',
            newPricingWindow,
            priceAdjustmentValue.value,
            keepExistingWindows.value,
            true
          );
        } else {
          const selectedPricingWindow = props.pricingWindows.find(
            (pw) => pw.id === copyToPricingWindow.value
          );

          emit(
            'price-window-copied',
            selectedPricingWindow,
            priceAdjustmentValue.value,
            keepExistingWindows.value,
            false
          );
        }

        hideModal();
      } catch (error) {
        toast.error('Failed to copy price window. Please try again.');
      }
    };

    return {
      copyPriceWindow,
      handleWindowChange,
      selectedPricingWindow: copyToPricingWindow,
      filteredPricingWindows,
      copyFrom,
      adjustmentInfo,
      createNewPricingWindow,
      priceAdjustmentValue,
      keepExistingWindows,
    };
  },
});
</script>

<style scoped>
.modal-content {
  text-align: left;
}

.icon-margin {
  margin-right: 10px;
}

.title {
  font-weight: 600;
  font-size: 0.8rem;
}

.form-select {
  width: auto;
  max-width: 90%;
  margin-top: 8px;
  font-weight: 500;
  font-size: 0.95rem;
}

.input-group {
  display: flex;
  align-items: center;
  width: 200px;
  margin-top: 8px;
}

.form-control {
  text-align: right;
  margin-left: 10px;
}

.input-group-text {
  background-color: #f5f5f5;
  padding: 0.375rem 0.75rem;
}
.adjustment-info {
  margin-top: 3px;
  font-size: 0.7rem;
}
.copy-from {
  font-weight: 400;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.keep-existing-windows-info {
  font-size: 0.8rem;
  position: relative;
  top: -2px;
}
</style>
